import { Injectable } from '@angular/core';
import { Store, StoreConfig } from '@datorama/akita';

import { GseApiConfig } from 'gse-api';
import { DEFAULT_STAMP_COLOR, User, YELLOW } from 'models';
import {
  ThemeMode,
  UserActionOverrides,
  UserUiSettings,
  UserUiSettingsSet,
  createEmptyUserUiSettingsSet,
} from 'src/app/models';
import { environment } from 'src/environments/environment';

import { DEFAULT_FONT_FAMILY } from 'common';
import { FavoriteDatabaseSearches } from './favorite-searches.service';
import { OpenDocumentSource } from './open-document-source';

/**
 * Application state.
 */
export interface ApplicationState extends UserUiSettings {
  /** Version of the connected API. */
  apiVersion: string;
  /** Whether the archive navigation section is expanded. */
  archiveNavSectionExpanded: boolean;
  /** An array of filenames used in enhanced export. */
  enhancedExportFilenameHistory: string[];
  /** Whether the favorited searches navigation section is expanded. */
  favoriteSearchesExpanded: boolean;
  /** Dictionary of favorited searches. */
  favoritedSearches: { [databaseId: string]: FavoriteDatabaseSearches };
  /** GlobalSearch Extensions configuration. */
  gseApiConfig: GseApiConfig;
  /** Whether the inbox navigation section is expanded. */
  inboxNavSectionExpanded: boolean;
  /** Whether the indexer sidebar is open. */
  indexerSidebarOpen: boolean;
  /**
   * Base instance URL.
   *
   * @example https://myserver
   */
  instanceUrl: string;
  /** Whether the left sidebar navigation is toggled. */
  leftSidebarNavHidden: boolean;
  /** System notifications enabled. */
  notifications: boolean;
  /** Open document. */
  openDocument?: OpenDocumentSource;
  /** PDF URL to be displayed in preview sidebar. */
  pdfPreviewUrl: string;
  /** ContentSearch terms to be displayed in the preview sidebar. */
  pdfPreviewContentSearchTerm: string;
  /** Whether the right sidebar is shown. */
  rightSidebarOpen: boolean;
  /** The database ID for which the search store is currently loaded. */
  searchStoreDatabaseId: string;
  /** Whether the task search section is expanded. */
  taskSearchSectionExpanded: boolean;
  /** Logged in User. Undefined when not logged in. */
  user?: User;
  /** Width of the document view sidebar. Undefined when not yet set by resizing.*/
  viewerRightSidebarWidth: number | undefined;
  /** Width of the document view sidebar when displaying a table field. Undefined when not yet set by resizing. */
  viewerRightSidebarTableFieldWidth: number | undefined;
  /** Whether the table field grid should be compact. */
  useCompactTableFieldGrid: boolean;
  /** State overrides to the user action buttons. */
  userActionButtonStateOverrides: UserActionOverrides;
  /** Which user settings have been set. */
  userUiSettingsSetByUser: UserUiSettingsSet;
}

/**
 * Creates a default user UI settings object.
 *
 * @returns Default user UI settings.
 */
export const createDefaultUserUiSettings = (): UserUiSettings => {
  // Use browsers preference for dark/light to decide our default value.
  const browserPreferesDark =
    window.matchMedia &&
    window.matchMedia('(prefers-color-scheme: dark)').matches;
  return {
    alwaysOpenNewTab: false,
    annotationNoteDefaultFillColor: YELLOW.hexCode,
    annotationNoteDefaultFontName: DEFAULT_FONT_FAMILY,
    annotationNoteDefaultFontSize: 70,
    annotationNoteDefaultOutlineOpacity: 1,
    annotationStampDefaultColor: DEFAULT_STAMP_COLOR.hexCode,
    annotationStampDefaultOpacity: 1,
    annotationStampDefaultThickness: 15,
    archiveHistoryResultsPerPage: 50,
    archiveResultsPerPage: 50,
    autoOpenFirstTableFieldOnIndexerLoad: false,
    autoSave: false,
    autoSaveOnVersioningArchives: false,
    autoSizeTextAreasToContentOnFocus: false,
    currentLanguage: 'English',
    customStamps: [],
    deleteInboxDocumentOnInsert: false,
    deleteSearchDocumentsOnInsert: false,
    dropdownToTypeaheadConversionThreshold: 1000,
    dxcAppendToMultivalue: false,
    dxcSearchSystemFields: true,
    doubleClickOpen: true,
    enabledTasksList: [],
    hideArchivesWithNoResults: true,
    indexerReloadAfterSave: false,
    internalViewerClearCacheOnReturnToSearch: false,
    keepBaseSearchDocumentOnInsert: true,
    keyfreeUseOcr: false,
    keyfreeAutoOcr: true,
    keyfreeAutoStartOnDocumentLoad: false,
    notificationPosition: 'toast-bottom-right',
    persistArchiveImportData: false,
    refreshResultsWhenClosingDocumentTabs: false,
    rightAlignNumericValuesInGrid: false,
    searchCountEnabled: false,
    showDomainInHistoryResults: false,
    showFavoriteSearches: true,
    showGlobalActionTasks: true,
    showTableFieldColumnsInGrid: false,
    showVersionsArchive: false,
    sortResultsArchivesByResultsCount: false,
    themeMode: browserPreferesDark ? ThemeMode.dark : ThemeMode.light,
    useBrowserLocaleToParseData: false,
    useCompactMode: false,
    usePreviousSearchCriteriaInRelatedSearch: false,
    defaultPageZoom: 'auto',
    viewerAutoSave: false,
    viewerOpenTableFieldsInSidebar: false,
    viewerUseInternal: true,
    viewerUseInternalForImport: true,
    showTableCellEditorButtons: false,
    showContentSearchTermsInPdfPreview: true,
  };
};

/**
 * Create the initial application state.
 *
 * @returns Default application state.
 */
export const createInitialState = (): ApplicationState => {
  return {
    ...createDefaultUserUiSettings(),
    apiVersion: '',
    archiveNavSectionExpanded: true,
    enhancedExportFilenameHistory: [],
    favoriteSearchesExpanded: true,
    favoritedSearches: {},
    gseApiConfig: {
      apiUrl: environment.gseUrlDefault,
    },
    inboxNavSectionExpanded: true,
    indexerSidebarOpen: true,
    instanceUrl: '',
    leftSidebarNavHidden: true,
    notifications: false,
    rightSidebarOpen: false,
    pdfPreviewUrl: '',
    pdfPreviewContentSearchTerm: '',
    user: undefined,
    useCompactTableFieldGrid: false,
    searchStoreDatabaseId: '',
    taskSearchSectionExpanded: true,
    userActionButtonStateOverrides: {},
    userUiSettingsSetByUser: createEmptyUserUiSettingsSet(),
    viewerRightSidebarWidth: undefined,
    viewerRightSidebarTableFieldWidth: undefined,
  };
};

/**
 * Applications store.
 */
@Injectable({ providedIn: 'root' })
@StoreConfig({ name: 'application' })
export class ApplicationStore extends Store<ApplicationState> {
  constructor() {
    super(createInitialState());
  }
}
